import { memo, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import formatMoney from '../../core/formatMoney';
import { apiRawFetch } from '../../main/client';

interface BreakdownBooking {
    id: number;
    guestName: string;
    checkin: string;
    checkout: string;
    invoiceId: string | null;
    revenue: number;
    currency: string;
    touristTax: number;
    source: 'invoice' | 'estimate';
}

interface BreakdownData {
    bookings: BreakdownBooking[];
    total: number;
    net: number;
    tax: number;
}

interface Props {
    propertyId: number;
    periodId: number;
}

const headerCellSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    py: 1.5,
    px: 2,
    color: '#9ca3af',
};

const TourismTaxBreakdownPanel = memo(({ propertyId, periodId }: Props) => {
    const [data, setData] = useState<BreakdownData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchBreakdown = async () => {
            setLoading(true);
            setError(null);
            const response = await apiRawFetch(
                `api/properties/${propertyId}/tax-periods/${periodId}/tourism-tax-breakdown`,
            );
            if (!response.ok) {
                setError('Failed to load breakdown');
                setLoading(false);
                return;
            }
            const json = await response.json();
            setData(json.data);
            setLoading(false);
        };
        fetchBreakdown();
    }, [propertyId, periodId]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ borderRadius: 0 }}>{error}</Alert>;
    }

    if (!data) return null;

    const bookingsWithoutInvoice = data.bookings.filter(b => !b.invoiceId);

    return (
        <Box sx={{ px: 2, pb: 2 }}>
            {bookingsWithoutInvoice.length > 0 && (
                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {bookingsWithoutInvoice.map(booking => (
                        <Alert key={booking.id} severity="warning" sx={{ borderRadius: 0, border: '2px solid #f59e0b' }}>
                            {booking.guestName} requires invoice!
                        </Alert>
                    ))}
                </Box>
            )}

            <Table size="small">
                <TableHead>
                    <TableRow sx={{ borderBottom: '2px solid #374151' }}>
                        <TableCell sx={headerCellSx}>Invoice</TableCell>
                        <TableCell sx={headerCellSx}>Guest</TableCell>
                        <TableCell sx={headerCellSx}>Checkin</TableCell>
                        <TableCell sx={headerCellSx}>Checkout</TableCell>
                        <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Revenue</TableCell>
                        <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Tax</TableCell>
                        <TableCell sx={headerCellSx}>Source</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.bookings.map(booking => (
                        <TableRow
                            key={booking.id}
                            sx={{
                                borderBottom: '1px solid #2d2d44',
                                '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.05)' },
                            }}
                        >
                            <TableCell sx={{ px: 2, fontWeight: 600, fontSize: '0.8rem' }}>
                                {booking.invoiceId ?? '-'}
                            </TableCell>
                            <TableCell sx={{ px: 2, fontSize: '0.8rem' }}>
                                {booking.guestName}
                            </TableCell>
                            <TableCell sx={{ px: 2, fontSize: '0.8rem' }}>
                                {booking.checkin}
                            </TableCell>
                            <TableCell sx={{ px: 2, fontSize: '0.8rem' }}>
                                {booking.checkout}
                            </TableCell>
                            <TableCell sx={{ px: 2, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                {formatMoney(booking.revenue, booking.currency)}
                            </TableCell>
                            <TableCell sx={{ px: 2, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, fontSize: '0.8rem', color: '#f59e0b' }}>
                                {formatMoney(booking.touristTax, 'HUF')}
                            </TableCell>
                            <TableCell sx={{ px: 2, fontSize: '0.7rem', textTransform: 'uppercase', color: '#6b7280' }}>
                                {booking.source}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Box
                sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: '2px solid #f59e0b',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Box sx={{ display: 'flex', gap: 3 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        Total: <Box component="span" sx={{ fontFamily: 'monospace' }}>{formatMoney(data.total, 'HUF')}</Box>
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        Net: <Box component="span" sx={{ fontFamily: 'monospace' }}>{formatMoney(data.net, 'HUF')}</Box>
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', color: '#f59e0b' }}>
                        Tax: <Box component="span" sx={{ fontFamily: 'monospace' }}>{formatMoney(data.tax, 'HUF')}</Box>
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        target="_blank"
                        variant="outlined"
                        size="small"
                        href="https://drive.istvan.xyz/d/f/sQgUmNiM4Ls2D9n9R5enZoGzJtgvZQlK"
                        sx={{ borderRadius: 0, borderWidth: '2px', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.625rem' }}
                    >
                        Get data XML
                    </Button>
                    <Button
                        target="_blank"
                        variant="contained"
                        size="small"
                        color="secondary"
                        href="https://ohp-20.asp.lgov.hu/nyitolap"
                        sx={{ borderRadius: 0, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.625rem' }}
                    >
                        Submit statement
                    </Button>
                </Box>
            </Box>
        </Box>
    );
});

export default TourismTaxBreakdownPanel;
